import React, { ComponentProps, useEffect, useState, useMemo } from 'react'
import SingleSearch from '@octanner/prism-core/SingleSearch'
import { SearchProps } from '@octanner/prism-core/SingleSearch/types'
import { useDebouncedValue } from '@octanner/prism-core/hooks'
import { useIdentitySearch } from '../../hooks/useIdentitySearch'
import {
  IdentityBase,
  IdentityWithErrors,
} from '../../models/CelebrationGroupEdit'

const isIdentityBase = (object?: object): object is IdentityBase =>
  object && 'id' in object
export const formatIdentityName = (identity: IdentityBase) =>
  `${identity.preferredName ?? ''} ${identity.lastName ?? ''}`.trim()

export interface Response {
  identitySearch: {
    identities: IdentityBase[]
  }
}

interface Props {
  'data-testid'?: string
  customerId: string
  onChange: (value?: IdentityBase) => void
  identity?: IdentityWithErrors
  renderOption?: ComponentProps<
    React.ComponentType<SearchProps>
  >['renderOption']
  placeholder?: string
  onError?: (error: { message: string } | null) => void
  options?: IdentityBase[]
}

//Reference: https://stackoverflow.com/a/13653180
const UUID_REGEX =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[0-5][0-9a-f]{3}-[089ab][0-9a-f]{3}-[0-9a-f]{12}$/i

const SingleSearchIdentitySelector = ({
  'data-testid': dataTestId,
  customerId,
  onChange,
  identity,
  placeholder,
  renderOption,
  onError,
}: Props) => {
  const [searchText, setSearchText] = useState('')
  const debouncedText = useDebouncedValue(searchText)

  const isUuid = UUID_REGEX.test(debouncedText)
  const results = useIdentitySearch({
    customerId,
    ...(isUuid ? { id: debouncedText } : { query: debouncedText }),
  })
  const optionsData = useMemo(
    () =>
      results.data?.identitySearch.identities
        ? results.data?.identitySearch.identities.map((i) => ({
            ...i,
            disabled: false,
          }))
        : [],
    [results.data?.identitySearch.identities]
  )

  useEffect(() => {
    if (results.error && onError) {
      onError(results.error)
    }
  }, [results.error, onError])

  return (
    <SingleSearch
      id="people-single-search"
      inputDataTestId={dataTestId}
      data-testid={dataTestId}
      // @ts-ignore this is a boolean prop
      multiple={false}
      loading={results.loading}
      // @ts-ignore bad typing from prism
      value={identity ?? ''}
      onChange={(_, selectedOptions) => {
        if (!isIdentityBase(selectedOptions)) return onChange()
        onChange(selectedOptions)
      }}
      onInputChange={(_, searchText, reason) => {
        if (reason === 'input') {
          setSearchText(searchText)
        } else if (reason === 'reset') {
          setSearchText('')
        }
      }}
      // @ts-ignore harcoded type used in component
      options={optionsData}
      placeholder={placeholder}
      getOptionLabel={formatIdentityName}
      renderOption={renderOption}
    />
  )
}

export default SingleSearchIdentitySelector
